// CHUNK 15: Modal with sharing options, Instagram story, WhatsApp, copy link, download.
import { useState, useRef } from 'react';
import { generateSynastryStoryCanvas } from '../../lib/synastryStoryGenerator';

interface Props {
  synastryId:  string;
  personAName: string;
  personBName: string;
  starScore:   number;
  onClose:     () => void;
}

export function ShareSynastryModal({ synastryId, personAName, personBName, starScore, onClose }: Props) {
  const [generating, setGenerating] = useState(false);
  const [imageUrl,   setImageUrl]   = useState<string | null>(null);
  const [copied,     setCopied]     = useState(false);
  const canvasRef                   = useRef<HTMLCanvasElement>(null);

  const shareUrl = `${window.location.origin}/sinastria/${synastryId}`;

  async function handleGenerateStory() {
    setGenerating(true);
    try {
      const canvas = document.createElement('canvas');
      await generateSynastryStoryCanvas(canvas, {
        personAName,
        personBName,
        starScore,
        synastryType: 'romantic',
      });
      const url = canvas.toDataURL('image/png');
      setImageUrl(url);
    } finally {
      setGenerating(false);
    }
  }

  function handleDownload() {
    if (!imageUrl) return;
    const a = document.createElement('a');
    a.href     = imageUrl;
    a.download = `professya-sinastria-${personAName}-${personBName}.png`.replace(/\s+/g, '-').toLowerCase();
    a.click();
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(shareUrl).catch(() => { prompt('Copie o link:', shareUrl); });
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  function handleWhatsApp() {
    const text = encodeURIComponent(
      `Descobri nossa conexão astrológica ✦ Veja aqui: ${shareUrl}`,
    );
    window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Compartilhar sinastria"
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/70 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0D1130] border border-[#C9A84C]/30 rounded-sm w-full max-w-sm p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[#C9A84C] text-sm uppercase tracking-[2px]">Compartilhar ✦</h2>
          <button onClick={onClose} className="text-[#F5ECD7]/40 hover:text-[#F5ECD7] text-lg">×</button>
        </div>

        {/* Story preview */}
        {imageUrl && (
          <div className="mb-5 border border-[#C9A84C]/20 rounded-sm overflow-hidden max-h-48 flex items-center justify-center bg-[#0D1130]/60">
            <img src={imageUrl} alt="Story preview" className="max-h-48 w-auto" />
          </div>
        )}

        {/* Actions */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={imageUrl ? handleDownload : handleGenerateStory}
            disabled={generating}
            className="flex flex-col items-center gap-2 border border-[#C9A84C]/30 p-4 text-[#F5ECD7]/80 text-xs hover:border-[#C9A84C]/60 transition-colors disabled:opacity-60"
          >
            {generating
              ? <span className="text-[#C9A84C] animate-pulse text-xl">✦</span>
              : <span className="text-xl">📸</span>
            }
            <span>{imageUrl ? 'Baixar imagem' : 'Gerar story'}</span>
          </button>

          <button
            onClick={handleWhatsApp}
            className="flex flex-col items-center gap-2 border border-[#C9A84C]/30 p-4 text-[#F5ECD7]/80 text-xs hover:border-[#C9A84C]/60 transition-colors"
          >
            <span className="text-xl">💬</span>
            <span>WhatsApp</span>
          </button>

          <button
            onClick={handleCopyLink}
            className="flex flex-col items-center gap-2 border border-[#C9A84C]/30 p-4 text-[#F5ECD7]/80 text-xs hover:border-[#C9A84C]/60 transition-colors"
          >
            <span className="text-xl">{copied ? '✓' : '🔗'}</span>
            <span>{copied ? 'Copiado!' : 'Copiar link'}</span>
          </button>

          {imageUrl && (
            <button
              onClick={handleDownload}
              className="flex flex-col items-center gap-2 border border-[#C9A84C]/30 p-4 text-[#F5ECD7]/80 text-xs hover:border-[#C9A84C]/60 transition-colors"
            >
              <span className="text-xl">⬇️</span>
              <span>Download PNG</span>
            </button>
          )}
        </div>

        <p className="text-center text-[#F5ECD7]/30 text-xs mt-4">
          Compartilhe e convide amigos a descobrirem suas conexões ✦
        </p>
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
}
